import { useRef, useState } from "react";
import clsx from "clsx";
import { Crosshair, Download, FileUp, MoreVertical, Table2, Trash2 } from "lucide-react";
import type { EditableLayerMerge, EditableLayerSummary, GeoJsonFeatureCollection } from "@/lib/types";
import type { EditableLayersState } from "@/lib/useEditableLayers";
import { editableLayerColor } from "@/lib/layerColors";
import { Banner, Button, ConfirmDialog, Panel, Spinner } from "./ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import EditableLayerTableDialog, { type AttributeEdits } from "./EditableLayerTableDialog";
import { useLanguage } from "@/i18n";
import { formatNumber } from "@/lib/format";

/**
 * Types an edited cell the way the value it replaces was typed.
 *
 * Numeric only when the stored value was already a number. Parsing anything
 * that merely LOOKS numeric would quietly rewrite identifiers from a
 * municipal register: a PLOT_NO of "0012" would become 12, losing a leading
 * zero that is part of the value.
 */
function coerceLike(previous: unknown, text: string): unknown {
  if (typeof previous === "number") {
    const asNum = text.trim() === "" ? NaN : Number(text);
    return Number.isFinite(asNum) ? asNum : text;
  }
  return text;
}

/** Triggers a browser download of `collection` as a .geojson file. Purely
 *  client-side -- the FeatureCollection is already held (or fetched on
 *  demand) in state, so there is nothing for the server to generate. */
function downloadGeoJson(filename: string, collection: unknown) {
  const blob = new Blob([JSON.stringify(collection, null, 2)], {
    type: "application/geo+json",
  });
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = /\.(geo)?json$/i.test(filename) ? filename : `${filename}.geojson`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Standalone GeoJSON overlays the office uploads for the field to work against.
 *
 * The third kind of geometry in this portal, and deliberately unlike the other
 * two. Zones/wards/blocks are the survey's structure and property outlines
 * become the survey itself -- both carry rules. A layer here carries none:
 * roads, drainage, a plan overlay, whatever reference geometry is useful on the
 * map. Nothing joins to it and deleting one loses only itself.
 *
 * **The geometry is edited in the field app, not here.** This panel manages the
 * file -- upload it, show it, hide it, throw it away -- and the
 * surveyor standing at the thing being mapped is the one who moves a line that
 * is in the wrong place. Correcting geometry from a desk against imagery that
 * may be months old is how a right line becomes a wrong one.
 */
export default function EditableLayersPanel({
  state,
  onZoomToLayer,
}: {
  state: EditableLayersState;
  /** Frame a layer's stored extent. Lives on the page because the map handle
   *  does, and this panel is a sibling of the map, not its parent. */
  onZoomToLayer: (bbox: [number, number, number, number]) => void;
}) {
  const { t, lang } = useLanguage();
  const {
    layers,
    data,
    visible,
    loadingIds,
    error,
    setError,
    loadLayer,
    saveLayer,
    toggleVisible,
    mergeFile,
    remove,
    upload,
  } = state;

  // "Update from file": the layer and file picked, the preview, the key used.
  const mergeInputRef = useRef<HTMLInputElement | null>(null);
  const [mergeLayer, setMergeLayer] = useState<EditableLayerSummary | null>(null);
  const [mergeFileState, setMergeFileState] = useState<File | null>(null);
  const [mergePlan, setMergePlan] = useState<EditableLayerMerge | null>(null);
  const [mergeBusy, setMergeBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function previewMerge(layer: EditableLayerSummary, file: File, key: string | null) {
    setMergeBusy(true);
    setError(null);
    try {
      setMergePlan(await mergeFile(layer.id, file, key, false));
    } catch (e) {
      setMergeLayer(null);
      setMergeFileState(null);
      setError(e instanceof Error ? e.message : t.editableLayersPanel.uploadFailed);
    } finally {
      setMergeBusy(false);
    }
  }

  async function applyMerge() {
    if (!mergeLayer || !mergeFileState || !mergePlan) return;
    setMergeBusy(true);
    try {
      const done = await mergeFile(mergeLayer.id, mergeFileState, mergePlan.match_key, true);
      setNotice(t.editableLayersPanel.mergeDone(mergeLayer.name, done.added, done.reshaped));
      setMergeLayer(null);
      setMergeFileState(null);
      setMergePlan(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.editableLayersPanel.uploadFailed);
    } finally {
      setMergeBusy(false);
    }
  }

  function closeMerge() {
    setMergeLayer(null);
    setMergeFileState(null);
    setMergePlan(null);
  }

  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<EditableLayerSummary | null>(null);
  const [tableLayer, setTableLayer] = useState<EditableLayerSummary | null>(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableSaving, setTableSaving] = useState(false);
  const [tableError, setTableError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  /** Opens the attribute-table dialog, fetching geometry first if this layer
   *  has never been switched on (View table doesn't require Show layer).
   *  Doesn't need loadLayer's return value the way exportLayer below does --
   *  the dialog reads `data[tableLayer.id]` as a prop, recomputed fresh on
   *  every render, so it picks up the fetch's result on its own once
   *  setTableLoading(false) below triggers the next render. */
  async function viewTable(layer: EditableLayerSummary) {
    setTableLayer(layer);
    // Only flash "Loading…" when there's actually a fetch to wait on --
    // loadLayer resolves instantly for an already-loaded layer, and toggling
    // the flag around a no-op await would still cost a visible flicker.
    if (!data[layer.id]) setTableLoading(true);
    await loadLayer(layer.id);
    setTableLoading(false);
  }

  /**
   * Applies the table's edited cells and writes the layer back.
   *
   * A cell is re-parsed as a number only when the value it replaces was
   * already one, so AREA__MTR stays numeric while a PLOT_NO of "0012" is not
   * quietly rewritten to 12 (see coerceLike). Blanking a cell stores an empty
   * string rather than dropping the key, so the column stays in the table
   * instead of vanishing for that row.
   *
   * Only `properties` is rebuilt -- geometry passes through byte-for-byte
   * from what the server last sent, which is what makes it safe to use the
   * whole-collection PUT for what is really an attribute edit.
   *
   * `close` is false when the table saved a single row: the operator is still
   * working through the others, so closing the dialog under them would be the
   * one action that throws away edits they have not committed yet.
   */
  async function saveTableEdits(edits: AttributeEdits, close: boolean) {
    const layer = tableLayer;
    const collection = layer ? data[layer.id] : undefined;
    if (!layer || !collection) return;

    const next: GeoJsonFeatureCollection = {
      ...collection,
      features: collection.features.map((f, i) => {
        const rowEdits = edits[i];
        if (!rowEdits) return f;
        const properties = { ...(f.properties ?? {}) };
        for (const [key, text] of Object.entries(rowEdits)) {
          properties[key] = coerceLike(properties[key], text);
        }
        return { ...f, properties };
      }),
    };

    setTableSaving(true);
    setTableError(null);
    try {
      await saveLayer(layer.id, next);
      if (close) setTableLayer(null);
    } catch (e) {
      // Kept open on failure, with the edits still in the table: they exist
      // only in this dialog, so closing it here is the one path that loses
      // someone's typing with nothing to recover it from.
      setTableError(e instanceof Error ? e.message : t.editableLayersPanel.couldntSaveChanges);
      // Rethrown so the dialog's own per-row handler knows not to clear that
      // row -- it has to stay pending for the retry.
      throw e;
    } finally {
      setTableSaving(false);
    }
  }

  /** A failed fetch here surfaces through the shared `error` banner above --
   *  loadLayer already calls setError internally, so there is nothing
   *  export-specific to add. */
  async function exportLayer(layer: EditableLayerSummary) {
    const collection = await loadLayer(layer.id);
    if (collection) downloadGeoJson(layer.name, collection);
  }

  async function handleFiles(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    if (!/\.(geo)?json$/i.test(file.name)) {
      setError(t.editableLayersPanel.onlyGeojsonAccepted);
      return;
    }
    setError(null);
    setUploading(file.name);
    try {
      await upload(file);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.editableLayersPanel.uploadFailed);
    } finally {
      setUploading(null);
      // Re-picking the same file after a failure fires no change event
      // otherwise, so a retry would look like a dead control.
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <Panel
      title={t.editableLayersPanel.panelTitle(layers?.length ?? 0)}
      className="h-full min-h-0"
      bodyClassName="flex flex-col min-h-0"
    >
      {error && (
        <div className="px-3 pt-3">
          <Banner tone="danger" action={<Button size="sm" onClick={() => setError(null)}>{t.common.dismiss}</Button>}>
            {error}
          </Banner>
        </div>
      )}
      {notice && (
        <div className="px-3 pt-3">
          <Banner tone="success" action={<Button size="sm" onClick={() => setNotice(null)}>{t.common.dismiss}</Button>}>
            {notice}
          </Banner>
        </div>
      )}

      <input
        ref={mergeInputRef}
        type="file"
        accept=".geojson,.json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file || !mergeLayer) return;
          if (!/\.(geo)?json$/i.test(file.name)) {
            setError(t.editableLayersPanel.onlyGeojsonAccepted);
            setMergeLayer(null);
            return;
          }
          setMergeFileState(file);
          void previewMerge(mergeLayer, file, null);
        }}
      />

      <div className="flex-none px-3 pt-3">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            void handleFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
          }}
          className={clsx(
            "cursor-pointer rounded border border-dashed px-3 py-2.5 text-center transition",
            dragActive
              ? "border-accent bg-accent-subtle"
              : "border-line-strong hover:border-ink-muted hover:bg-surface-sunken"
          )}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".geojson,.json"
            className="hidden"
            onChange={(e) => void handleFiles(e.target.files)}
          />
          <p className="text-xs text-ink-secondary">
            {uploading ? t.editableLayersPanel.uploadingFile(uploading) : t.editableLayersPanel.dropHint}
          </p>
          <p className="mt-0.5 text-2xs text-ink-muted">
            {t.editableLayersPanel.uploadHint}
          </p>
        </div>
      </div>

      <div className="thin-scroll min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {layers === null ? (
          <p className="flex items-center gap-2 text-2xs text-ink-muted">
            <Spinner className="h-3 w-3" /> {t.editableLayersPanel.loadingUploads}
          </p>
        ) : layers.length === 0 ? (
          <p className="px-1 py-2 text-2xs text-ink-muted">
            {t.editableLayersPanel.noLayersYet}
          </p>
        ) : (
          <ul className="space-y-0.5">
            {layers.map((layer) => {
              const on = !!visible[layer.id];
              return (
                <li
                  key={layer.id}
                  className="flex items-center gap-2 rounded px-1.5 py-1 hover:bg-surface-sunken"
                >
                  <button
                    type="button"
                    onClick={() => toggleVisible(layer.id)}
                    aria-pressed={on}
                    title={on ? t.editableLayersPanel.hideLayer : t.editableLayersPanel.showLayer}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                  >
                    <span
                      className={clsx("h-2.5 w-2.5 flex-none rounded-sm", !on && "opacity-25")}
                      style={{ backgroundColor: editableLayerColor(layer.id) }}
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className={clsx(
                          "block truncate text-xs",
                          on ? "text-ink" : "text-ink-muted line-through decoration-line-strong"
                        )}
                        title={layer.name}
                      >
                        {layer.name}
                      </span>
                      <span className="block truncate text-2xs text-ink-muted">
                        {formatNumber(layer.feature_count, lang)} {layer.geometry_type.toLowerCase()}
                        {layer.feature_count === 1 ? "" : "s"}
                      </span>
                    </span>
                    {loadingIds.has(layer.id) && <Spinner className="h-3 w-3 flex-none" />}
                  </button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        aria-label={t.editableLayersPanel.actionsFor(layer.name)}
                        className="flex h-6 w-6 flex-none items-center justify-center rounded text-ink-muted transition hover:bg-surface-raised hover:text-ink"
                      >
                        <MoreVertical className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onSelect={() => toggleVisible(layer.id)}>
                        {on ? t.editableLayersPanel.hideLayerMenu : t.editableLayersPanel.showLayerMenu}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={!layer.bbox}
                        onSelect={() => layer.bbox && onZoomToLayer(layer.bbox)}
                      >
                        <Crosshair className="mr-2 h-3.5 w-3.5" />
                        {t.editableLayersPanel.zoomToLayer}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {/* Read-only views of what's actually in the layer right
                          now -- including anything the field has already
                          corrected, since this reads the live geojson, the
                          same thing the map and a PUT from the field app see. */}
                      <DropdownMenuItem onSelect={() => void viewTable(layer)}>
                        <Table2 className="mr-2 h-3.5 w-3.5" />
                        {t.editableLayersPanel.viewTable}
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => void exportLayer(layer)}>
                        <Download className="mr-2 h-3.5 w-3.5" />
                        {t.editableLayersPanel.export}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => {
                          setNotice(null);
                          setMergeLayer(layer);
                          mergeInputRef.current?.click();
                        }}
                      >
                        <FileUp className="mr-2 h-3.5 w-3.5" />
                        {t.editableLayersPanel.updateFromFile}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-status-text-critical"
                        onSelect={() => setConfirmDelete(layer)}
                      >
                        <Trash2 className="mr-2 h-3.5 w-3.5" />
                        {t.editableLayersPanel.deleteLayer}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete !== null}
        title={t.editableLayersPanel.deleteLayerTitle}
        body={
          <>
            <span className="font-medium text-ink">{confirmDelete?.name}</span>{" "}
            {t.editableLayersPanel.deleteLayerBodySuffix(
              formatNumber(confirmDelete?.feature_count ?? 0, lang)
            )}
          </>
        }
        confirmLabel={t.editableLayersPanel.deleteLayerConfirmLabel}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          const target = confirmDelete;
          setConfirmDelete(null);
          if (target) void remove(target.id);
        }}
      />


      <ConfirmDialog
        open={mergeLayer !== null && mergePlan !== null}
        title={t.editableLayersPanel.mergeTitle(mergeLayer?.name ?? "")}
        danger={false}
        busy={mergeBusy}
        confirmDisabled={
          !mergePlan || mergePlan.added + mergePlan.reshaped + mergePlan.attributes_added === 0
        }
        confirmLabel={t.editableLayersPanel.mergeApply}
        onCancel={closeMerge}
        onConfirm={() => void applyMerge()}
        body={
          mergePlan && (
            <div className="space-y-2">
              <p>{t.editableLayersPanel.mergeIntro(mergeFileState?.name ?? "")}</p>
              <label className="flex items-center gap-2 text-xs">
                <span className="text-ink-secondary">{t.editableLayersPanel.mergeKey}</span>
                <select
                  className="h-7 rounded border border-line bg-surface-sunken px-1 text-xs"
                  value={mergePlan.match_key ?? ""}
                  disabled={mergeBusy}
                  onChange={(e) =>
                    mergeLayer && mergeFileState &&
                    void previewMerge(mergeLayer, mergeFileState, e.target.value || null)
                  }
                >
                  <option value="">{t.editableLayersPanel.mergeNoKey}</option>
                  {Array.from(new Set([...(mergePlan.match_key ? [mergePlan.match_key] : []), ...mergePlan.key_choices])).map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
              </label>
              <ul className="space-y-0.5 text-xs">
                <li><b>{formatNumber(mergePlan.added, lang)}</b> {t.editableLayersPanel.mergeAdded}</li>
                <li><b>{formatNumber(mergePlan.reshaped, lang)}</b> {t.editableLayersPanel.mergeReshaped}</li>
                {mergePlan.attributes_added > 0 && (
                  <li><b>{formatNumber(mergePlan.attributes_added, lang)}</b> {t.editableLayersPanel.mergeAttrs}</li>
                )}
                <li className="text-ink-muted"><b>{formatNumber(mergePlan.unchanged, lang)}</b> {t.editableLayersPanel.mergeUnchanged}</li>
                {mergePlan.duplicates_in_file + mergePlan.ambiguous + mergePlan.invalid_skipped > 0 && (
                  <li className="text-ink-muted">
                    <b>{formatNumber(mergePlan.duplicates_in_file + mergePlan.ambiguous + mergePlan.invalid_skipped, lang)}</b>{" "}
                    {t.editableLayersPanel.mergeSkipped}
                  </li>
                )}
              </ul>
              <p className="text-2xs text-ink-muted">
                {t.editableLayersPanel.mergeNote(formatNumber(mergePlan.feature_count, lang))}
              </p>
            </div>
          )
        }
      />

      <EditableLayerTableDialog
        layer={tableLayer}
        collection={tableLayer ? data[tableLayer.id] : undefined}
        loading={tableLoading}
        saving={tableSaving}
        error={tableError}
        onOpenChange={(open) => {
          if (!open) {
            setTableLayer(null);
            setTableError(null);
          }
        }}
        onSave={saveTableEdits}
      />
    </Panel>
  );
}
